import { useEffect, useState } from "react";
import { X, Heart, Shield, Loader2, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * MetaShopModal — permanent between-run upgrades (iter66).
 * Spent currency: `essence` (earned on EVERY death + victory).
 * Each tier bumps either Max HP or Base Defense permanently across all runs.
 */
export default function MetaShopModal({ open, onClose, onPurchased }) {
  const [shop, setShop] = useState(null);
  const [busy, setBusy] = useState(null); // "hp" | "armor" | null

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await api.get("/meta/shop");
        setShop(s);
      } catch {
        toast.error("The forge is silent — try again.");
      }
    })();
  }, [open]);

  if (!open) return null;

  const buy = async (track) => {
    if (busy) return;
    setBusy(track);
    try {
      const r = await api.post("/meta/upgrade", { track });
      if (r?.ok) {
        toast.success(`${track === "hp" ? "Flesh" : "Hide"} tempered — tier ${r.new_tier}.`);
        const s = await api.get("/meta/shop");
        setShop(s);
        onPurchased?.(r);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed.");
    }
    setBusy(null);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 backdrop-blur-sm px-3 py-6 overflow-y-auto"
      data-testid="meta-shop-modal"
    >
      <div
        className="panel w-full max-w-xl p-5 sm:p-7 relative my-auto"
        style={{ borderColor: "#d4af37", boxShadow: "0 0 60px rgba(212,175,55,0.30)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#8b7965] hover:text-[#f0e6d2]"
          data-testid="meta-shop-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="text-center mb-5">
          <div className="text-[10px] tracking-[0.5em] gold-text">— THE FORGE OF ASHES —</div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#f0e6d2] italic mt-2">
            Permanent Tempering
          </h2>
          <div className="mt-3 inline-flex items-center gap-2 panel px-3 py-1.5">
            <Flame size={14} className="ember" />
            <span className="text-[11px] tracking-widest ash-text">ESSENCE</span>
            <span className="font-heading text-lg gold-text" data-testid="meta-essence-balance">
              {shop?.essence ?? "—"}
            </span>
          </div>
          <p className="text-[11px] ash-text italic mt-3 leading-relaxed max-w-sm mx-auto">
            Every fall grants essence. Spend it here to make your next descent harder to kill.
            <br />Carries across every run, every class, forever.
          </p>
        </div>

        {!shop ? (
          <div className="flex items-center justify-center py-10 ash-text">
            <Loader2 size={16} className="animate-spin mr-2" /> Reading the forge-fire...
          </div>
        ) : (
          <div className="space-y-3">
            {["hp", "armor"].map((track) => {
              const u = shop.upgrades[track];
              const canAfford = u.next_cost != null && (shop.essence ?? 0) >= u.next_cost;
              const maxed = u.tier >= u.max_tier;
              const Icon = track === "hp" ? Heart : Shield;
              return (
                <div
                  key={track}
                  className="panel p-4"
                  data-testid={`meta-upgrade-${track}`}
                  style={{ borderColor: track === "hp" ? "rgba(226,88,34,0.5)" : "rgba(100,160,220,0.5)" }}
                >
                  <div className="flex items-start gap-3">
                    <Icon size={28} className={track === "hp" ? "ember" : ""} style={track === "armor" ? { color: "#64a0dc" } : {}} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <div className="font-heading text-base sm:text-lg text-[#f0e6d2] leading-tight">{u.name}</div>
                          <div className="text-[11px] ash-text italic mt-0.5">{u.desc}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] tracking-widest ash-text">TIER</div>
                          <div className="font-heading text-xl gold-text">{u.tier} / {u.max_tier}</div>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3 h-1.5 bg-black/60 relative overflow-hidden rounded-sm">
                        <div
                          className="absolute inset-y-0 left-0 transition-all"
                          style={{
                            width: `${(u.tier / u.max_tier) * 100}%`,
                            background: track === "hp"
                              ? "linear-gradient(90deg, #e25822, #d4af37)"
                              : "linear-gradient(90deg, #64a0dc, #d4af37)",
                          }}
                        />
                      </div>
                      {/* Current / Next */}
                      <div className="flex items-center justify-between mt-2 text-[10px] tracking-widest ash-text">
                        <span>CURRENT: <span className="gold-text">+{u.current_bonus}{track === "hp" ? " HP" : " DEF"}</span></span>
                        {!maxed && (
                          <span>NEXT: <span className="gold-text">+{u.bonus_per_tier}{track === "hp" ? " HP" : " DEF"}</span></span>
                        )}
                      </div>
                      {/* Buy button */}
                      <button
                        onClick={() => buy(track)}
                        disabled={maxed || !canAfford || busy === track}
                        data-testid={`meta-upgrade-${track}-buy`}
                        className="mt-3 w-full btn-primary py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ borderColor: canAfford && !maxed ? "#d4af37" : "rgba(139,121,101,0.5)" }}
                      >
                        {busy === track ? (
                          <><Loader2 size={14} className="animate-spin" /> TEMPERING...</>
                        ) : maxed ? (
                          "FULLY TEMPERED"
                        ) : canAfford ? (
                          <>FORGE · <Flame size={12} /> {u.next_cost}</>
                        ) : (
                          <>Need {u.next_cost - (shop.essence ?? 0)} more essence</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
